/**
 * Detection for the Capacitor iOS shell. capacitor.config.ts appends this
 * token to the WKWebView user agent (iOS and iPad blocks both).
 *
 * The main consumer is App Store Guideline 3.1.1 / 3.1.3(c): the app is sold
 * to organisations, so the shell shows no billing at all — no checkout or
 * portal entry point, and no plan price, card on file or overage notice
 * either. App Review rejected build 9 (2026-10-17) on a read-only plan +
 * price panel, so "display only" is not safe.
 */
export const NATIVE_APP_UA_TOKEN = "SurveyThanksCapacitor"

export function isNativeAppUA(userAgent: string | null | undefined): boolean {
  return (userAgent ?? "").includes(NATIVE_APP_UA_TOKEN)
}

/**
 * The native apps never create an account (App Store 3.1.3(c) — the service is
 * sold to organisations, and the app is where their staff sign in). Shown when
 * someone with no account, and no invitation, tries to sign in from the app —
 * by email or with Apple/Microsoft. Lives here, not in lib/signup-gate.ts, so
 * the client-side sign-in buttons can show the same words without pulling in
 * Prisma.
 */
export const NATIVE_APP_NO_ACCOUNT_MESSAGE =
  "There's no account for this email. Ask your organisation's administrator to invite you, then sign in here."

/** Client-side check. False during SSR — gate with an effect where a hydration mismatch matters. */
export function isNativeApp(): boolean {
  return typeof navigator !== "undefined" && isNativeAppUA(navigator.userAgent)
}

/**
 * Is this request coming from the native shell running on a *phone*?
 *
 * Both the iOS and Android Capacitor builds append the same UA token, and the
 * iPad build is a deliberately different product surface (it is the form-filling
 * tablet — see the minimal-HTML renderer note in capacitor.config.ts), so the
 * token alone can't answer this. The form-factor read is UA-shaped:
 *
 *   - iPad first, before the generic checks: an iPadOS WKWebView still carries
 *     `Mobile/15E148`, so testing for "Mobile" ahead of it would call every
 *     iPad a phone.
 *   - Android tablets send the same UA as phones minus the `Mobile` token —
 *     that omission is the only signal Chrome/WebView gives.
 *   - Anything else (an iPad requesting the desktop UA reports `Macintosh`)
 *     falls through to false, so an unrecognised shell keeps the web behaviour
 *     rather than being pushed down a phone-only path.
 */
export function isNativePhoneUA(userAgent: string | null | undefined): boolean {
  if (!isNativeAppUA(userAgent)) return false
  const ua = userAgent ?? ""
  if (/iPad/i.test(ua)) return false
  if (/iPhone|iPod/i.test(ua)) return true
  if (/Android/i.test(ua)) return /Mobile/i.test(ua)
  return false
}

/**
 * Pages the native shell never shows (App Store Guideline 3.1.1 / 3.1.3(c)):
 * the marketing site, the price list and self-serve sign-up. Inside the app a
 * reviewer who reached /pricing saw prices and "Get started" buttons that buy
 * outside IAP, so middleware sends these to "/" — which itself goes to sign-in
 * (or /resume when signed in). Prefixes match whole segments: "/for" is not
 * "/forms". Legal pages (/privacy, /terms, /support) stay reachable.
 */
const NATIVE_HIDDEN_PREFIXES = ["/pricing", "/franchises", "/for", "/templates", "/sign-up", "/email-entry/sign-up"]

export function isHiddenInNativeApp(pathname: string): boolean {
  return NATIVE_HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}
